import React, { Fragment, useRef, useState } from "react";
import { Row, Col, Card, Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import SignatureCanvas from "react-signature-canvas";
import swal from "sweetalert";
import api from "../../../api";

const OtherMomAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    meetingDate: "",
    preparedBy: "",
    duration: "",
    fromTime: "",
    toTime: "",
    venue: "",
    subject: "",
    objective: "",
    companyName: "",
    clientName: "",
  });

  const [minutes, setMinutes] = useState([""]);
  const [hostParticipants, setHostParticipants] = useState([""]);
  const [clientParticipants, setClientParticipants] = useState([""]);

  // Signature
  const clientSigRef = useRef(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSignature, setClientSignature] = useState(null);

  // ================= HANDLERS =================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Minutes
  const addMinute = () => setMinutes([...minutes, ""]);
  const removeMinute = (i) => setMinutes(minutes.filter((_, idx) => idx !== i));
  const handleMinuteChange = (i, val) => {
    const updated = [...minutes];
    updated[i] = val;
    setMinutes(updated);
  };

  // Participants
  const addHost = () => setHostParticipants([...hostParticipants, ""]);
  const addClient = () => setClientParticipants([...clientParticipants, ""]);
  const handleHostChange = (i, val) => {
    const updated = [...hostParticipants];
    updated[i] = val;
    setHostParticipants(updated);
  };
  const handleClientChange = (i, val) => {
    const updated = [...clientParticipants];
    updated[i] = val;
    setClientParticipants(updated);
  };

  // Signature helpers
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const saveClientSignature = () => {
    if (clientSigRef.current.isEmpty())
      return swal("Error", "Please sign before saving", "error");
    const dataURL = clientSigRef.current.toDataURL("image/png");
    const file = new File([dataURLtoBlob(dataURL)], "client-signature.png", {
      type: "image/png",
    });
    setClientSignature(file);
    setShowClientModal(false);
  };

  const clearClientSignature = () => {
    clientSigRef.current.clear();
    setClientSignature(null);
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.meetingDate)
      return swal("Warning", "Meeting Date is required", "warning");
    if (!formData.preparedBy)
      return swal("Warning", "Prepared By is required", "warning");
    if (minutes.some((m) => !m.trim()))
      return swal("Warning", "All minutes points are required", "warning");

    try {
      setLoading(true);
      const payload = new FormData();

      Object.keys(formData).forEach((key) =>
        payload.append(key, formData[key])
      );
      payload.append("minutes", JSON.stringify(minutes));
      payload.append("hostParticipants", JSON.stringify(hostParticipants));
      payload.append("clientParticipants", JSON.stringify(clientParticipants));

      if (clientSignature) payload.append("clientSignature", clientSignature);

      const res = await api.post("/other-mom", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        swal("Success", "MOM Created Successfully", "success");
        navigate("/other-mom/list");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================
  return (
    <Fragment>
      <PageTitle activeMenu="Add Other MOM" motherMenu="MOM" />

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <Card.Title>Add Other Minutes Of Meeting</Card.Title>
              <Button variant="secondary" onClick={() => navigate("/other-mom/list")}>
                <i className="fas fa-arrow-left me-2"></i>Back
              </Button>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* ===== MEETING DETAILS ===== */}
                <h5 className="mb-3">Meeting Details</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Meeting Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="meetingDate"
                        value={formData.meetingDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prepared By *</Form.Label>
                      <Form.Control
                        type="text"
                        name="preparedBy"
                        value={formData.preparedBy}
                        onChange={handleChange}
                        placeholder="Enter name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration</Form.Label>
                      <Form.Control
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        placeholder="e.g. 2 Hr"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>From Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="fromTime"
                        value={formData.fromTime}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>To Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="toTime"
                        value={formData.toTime}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Venue</Form.Label>
                      <Form.Control
                        type="text"
                        name="venue"
                        value={formData.venue}
                        onChange={handleChange}
                        placeholder="Enter venue"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Subject */}
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subject / Topic</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="e.g. Internal review, Vendor discussion..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* ===== PARTICIPANTS ===== */}
                <h5 className="mt-4 mb-3">Attended By</h5>
                <Row>
                  <Col md={6}>
                    <h6>Host Participants</h6>
                    {hostParticipants.map((host, i) => (
                      <Form.Group key={i} className="mb-2">
                        <Form.Control
                          type="text"
                          value={host}
                          onChange={(e) => handleHostChange(i, e.target.value)}
                          placeholder={`Host ${i + 1}`}
                        />
                      </Form.Group>
                    ))}
                    <Button size="sm" variant="success" onClick={addHost}>
                      + Add Host
                    </Button>
                  </Col>
                  <Col md={6}>
                    <h6>Other Participants</h6>
                    {clientParticipants.map((p, i) => (
                      <Form.Group key={i} className="mb-2">
                        <Form.Control
                          type="text"
                          value={p}
                          onChange={(e) => handleClientChange(i, e.target.value)}
                          placeholder={`Participant ${i + 1}`}
                        />
                      </Form.Group>
                    ))}
                    <Button size="sm" variant="success" onClick={addClient}>
                      + Add Participant
                    </Button>
                  </Col>
                </Row>

                {/* ===== OBJECTIVE ===== */}
                <h5 className="mt-4 mb-3">Objectives of Meeting</h5>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="objective"
                    value={formData.objective}
                    onChange={handleChange}
                    placeholder="Enter meeting objectives"
                  />
                </Form.Group>

                {/* ===== MINUTES ===== */}
                <h5 className="mt-4 mb-3">Minutes of the Meeting</h5>
                {minutes.map((minute, i) => (
                  <Card key={i} className="mb-2 p-3">
                    <div className="d-flex justify-content-between">
                      <strong>Point {i + 1}</strong>
                      {minutes.length > 1 && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => removeMinute(i)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      )}
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="mt-2"
                      value={minute}
                      onChange={(e) => handleMinuteChange(i, e.target.value)}
                    />
                  </Card>
                ))}
                <Button variant="primary" size="sm" onClick={addMinute}>
                  + Add Point
                </Button>

                {/* ===== SIGNATURE ===== */}
                <h5 className="mt-4 mb-3">Signature Section</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>For Company</Form.Label>
                      <Form.Control
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Company Name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>For Client / Attendee</Form.Label>
                      <Form.Control
                        type="text"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleChange}
                        placeholder="Name"
                      />
                    </Form.Group>
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="warning"
                        size="sm"
                        onClick={() => setShowClientModal(true)}
                      >
                        Add Signature
                      </Button>
                      {clientSignature && (
                        <span className="text-success ms-2">✓ Signed</span>
                      )}
                    </div>
                  </Col>
                </Row>

                <div className="mt-4 d-flex gap-2">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? "Saving..." : "Save MOM"}
                  </Button>
                  <Button variant="danger" onClick={() => navigate("/other-mom/list")}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Signature Modal */}
      <Modal show={showClientModal} onHide={() => setShowClientModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Client / Attendee Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ width: "100%", height: "200px", border: "2px dashed #999" }}>
            <SignatureCanvas
              ref={clientSigRef}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: "w-100 h-100" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={clearClientSignature}>Clear</Button>
          <Button variant="success" onClick={saveClientSignature}>Save Signature</Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default OtherMomAdd;
